import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Barcode, Camera, MagnifyingGlass, Plus, X } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { photoSrc } from "@/components/AppHeader";
import { BarcodeScanDialog } from "@/components/BarcodeScanDialog";

const firstPhoto = (item) => {
  const p = (item.photos || []).find((x) => !x.is_deleted) || (item.photos || [])[0];
  return p ? photoSrc(p.path || p.url || p) : null;
};

const dims = (item) => {
  const l = item.length_in;
  const w = item.width_in;
  const th = item.thickness;
  const size = l && w ? `${Math.round(l)}″ × ${Math.round(w)}″` : "";
  return [size, th].filter(Boolean).join(" · ");
};

const StatCard = ({ value, label, tone, testid }) => (
  <div
    data-testid={testid}
    className="rounded-2xl border border-zinc-800 bg-[#0F172A]/60 px-3 py-3.5 text-center"
  >
    <div className={`font-mono-data text-2xl font-bold leading-none ${tone}`}>{value}</div>
    <div className="mt-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-500">
      {label}
    </div>
  </div>
);

const inp =
  "mt-1 h-11 w-full rounded-xl border border-zinc-800 bg-[#080C14] px-3 text-[15px] text-zinc-100 placeholder:text-zinc-600 focus:border-[#c39e6d] focus:outline-none";
const Lbl = ({ children }) => (
  <label className="mt-3 block text-[10px] font-bold uppercase tracking-widest text-zinc-500">
    {children}
  </label>
);
const chip = (on) =>
  `rounded-lg border px-3 py-2 text-[11px] font-bold uppercase tracking-widest transition-colors duration-150 ${
    on
      ? "border-[#c39e6d] bg-[#c39e6d]/15 text-[#c39e6d]"
      : "border-zinc-800 bg-[#080C14] text-zinc-400 hover:border-zinc-700"
  }`;
const blankForm = () => ({
  kind: "slab",
  material: "Granite",
  slab_color: "",
  brand: "",
  supplier: "",
  thickness: "3cm",
  finish: "Polished",
  length: "",
  width: "",
  location: "",
  quantity: "1",
  status: "In Stock",
  notes: "",
  file: null,
});
const fromItem = (it) => ({
  kind: it._kind || "slab",
  material: it.material || "Granite",
  slab_color: it.slab_color || "",
  brand: it.brand || "",
  supplier: it.supplier || "",
  thickness: it.thickness || "3cm",
  finish: it.finish || "Polished",
  length: it.length_in ? String(it.length_in) : "",
  width: it.width_in ? String(it.width_in) : "",
  location: it.location || "",
  quantity: it.quantity != null ? String(it.quantity) : "1",
  status: it.status || "In Stock",
  notes: it.notes || "",
  file: null,
});

const StockCard = ({ item, onOpen }) => {
  const photo = firstPhoto(item);
  const badge = item._kind === "remnant" ? "Remnant" : item.status === "In Stock" ? "Shop floor" : item.status;
  const badgeCls =
    item._kind === "remnant"
      ? "border-amber-500/40 bg-amber-500/15 text-amber-200"
      : "border-zinc-700 bg-[#080C14]/70 text-zinc-200";
  return (
    <button
      type="button"
      onClick={() => onOpen(item)}
      data-testid={`mobile-stock-card-${item.id}`}
      className="overflow-hidden rounded-2xl border border-zinc-800 bg-[#0F172A]/60 text-left transition-all duration-150 hover:border-zinc-700 active:scale-[0.99]"
    >
      <div className="relative aspect-[5/4] w-full bg-zinc-800">
        {photo ? (
          <img src={photo} alt={item.slab_color} className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-zinc-700/40 to-zinc-900 text-[10px] font-bold uppercase tracking-widest text-zinc-600">
            {item.material || "Slab"}
          </div>
        )}
        <span
          className={`absolute left-2 top-2 rounded-md border px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest ${badgeCls}`}
        >
          {badge}
        </span>
      </div>
      <div className="p-3">
        {(item.brand || item.supplier) && (
          <div className="truncate text-[10px] font-bold uppercase tracking-widest text-[#c39e6d]">
            {item.brand || item.supplier}
          </div>
        )}
        <div className="mt-0.5 truncate text-[15px] font-bold text-zinc-100">
          {item.slab_color || "Unnamed"}
        </div>
        {dims(item) && (
          <div className="mt-0.5 font-mono-data text-[11px] text-zinc-500">{dims(item)}</div>
        )}
      </div>
    </button>
  );
};

export default function MobileStock() {
  const [slabs, setSlabs] = useState([]);
  const [remnants, setRemnants] = useState([]);
  const [slabStats, setSlabStats] = useState(null);
  const [remStats, setRemStats] = useState(null);
  const [q, setQ] = useState("");
  const fileRef = useRef(null);
  const [capturing, setCapturing] = useState(false);
  const [tag, setTag] = useState(null);
  const [detail, setDetail] = useState(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(blankForm);
  const [busy, setBusy] = useState(false);
  const addFileRef = useRef(null);
  const [scanOpen, setScanOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [needsPhoto, setNeedsPhoto] = useState(false);
  const detailFileRef = useRef(null);
  const [snapping, setSnapping] = useState(false);

  // Saw crew: snap the real stone onto a remnant/slab record so the visualiser shows it
  const snapDetailPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !detail) return;
    setSnapping(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const url = detail._kind === "remnant" ? `/remnants/${detail.id}/photos` : `/slabs/${detail.id}/photos`;
      const { data } = await api.post(url, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const upd = { ...data, _kind: detail._kind };
      setDetail(upd);
      (detail._kind === "remnant" ? setRemnants : setSlabs)((list) => list.map((it) => (it.id === data.id ? data : it)));
      toast.success("Photo saved — the visualiser now shows the real stone");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Photo upload failed");
    }
    setSnapping(false);
  };

  const saveAdd = async () => {
    const f = form;
    if (!f.slab_color.trim()) {
      toast.error("Name / color is required");
      return;
    }
    const L = parseFloat(f.length) || 0;
    const W = parseFloat(f.width) || 0;
    if (f.kind === "remnant" && (L <= 0 || W <= 0)) {
      toast.error("A remnant needs length & width");
      return;
    }
    setBusy(true);
    try {
      let created;
      const common = {
        slab_color: f.slab_color.trim(),
        material: f.material,
        brand: f.brand.trim(),
        supplier: f.supplier.trim(),
        thickness: f.thickness,
        finish: f.finish,
        length_in: L,
        width_in: W,
        location: f.location.trim(),
        notes: f.notes.trim(),
      };
      const slabBody = { ...common, quantity: parseInt(f.quantity, 10) || 1, status: f.status };
      const remBody = { ...common, usable_sqft: Math.round(((L * W) / 144) * 10) / 10 };
      if (editItem) {
        const url = f.kind === "slab" ? `/slabs/${editItem.id}` : `/remnants/${editItem.id}`;
        ({ data: created } = await api.put(url, {
          ...editItem,
          ...(f.kind === "slab" ? slabBody : remBody),
        }));
      } else if (f.kind === "slab") {
        ({ data: created } = await api.post("/slabs", slabBody));
      } else {
        ({ data: created } = await api.post("/remnants", remBody));
      }
      if (f.file) {
        const fd = new FormData();
        fd.append("file", f.file);
        const url = f.kind === "slab" ? `/slabs/${created.id}/photos` : `/remnants/${created.id}/photos`;
        ({ data: created } = await api.post(url, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        }));
      }
      const upd = (l) =>
        editItem ? l.map((x) => (x.id === created.id ? created : x)) : [created, ...l];
      if (f.kind === "slab") setSlabs(upd);
      else setRemnants(upd);
      toast.success(editItem ? "Saved" : f.kind === "slab" ? "Slab added" : "Remnant added");
      setAdding(false);
      setEditItem(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save");
    }
    setBusy(false);
  };

  const onCapture = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setCapturing(true);
    try {
      const { data: rem } = await api.post("/remnants", {
        slab_color: "Field capture",
        length_in: 12,
        width_in: 12,
        source_tag: "Field capture",
      });
      const fd = new FormData();
      fd.append("file", file);
      const { data: withPhoto } = await api.post(`/remnants/${rem.id}/photos`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setRemnants((list) => [withPhoto, ...list]);
      setTag({ rem: withPhoto, name: "", location: "", length: "", width: "", thickness: "3cm", material: "Granite" });
      toast.success("Slab captured — add a name & location");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Capture failed");
    }
    setCapturing(false);
  };

  const saveTag = async () => {
    const { rem, name, location, length, width, thickness, material } = tag;
    const l = parseFloat(length);
    const w = parseFloat(width);
    const dims =
      l > 0 && w > 0
        ? { length_in: l, width_in: w, usable_sqft: Math.round(((l * w) / 144) * 10) / 10 }
        : {};
    try {
      const { data } = await api.put(`/remnants/${rem.id}`, {
        ...rem,
        slab_color: name.trim() || rem.slab_color,
        location: location.trim(),
        thickness: thickness || rem.thickness,
        material: material || rem.material,
        ...dims,
      });
      setRemnants((list) => list.map((r) => (r.id === data.id ? data : r)));
      toast.success("Slab tagged");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save");
    }
    setTag(null);
  };

  useEffect(() => {
    api.get("/slabs").then(({ data }) => setSlabs(data)).catch(() => {});
    api.get("/remnants").then(({ data }) => setRemnants(data)).catch(() => {});
    api.get("/slabs/stats").then(({ data }) => setSlabStats(data)).catch(() => {});
    api.get("/remnants/stats").then(({ data }) => setRemStats(data)).catch(() => {});
  }, []);

  const items = useMemo(() => {
    const merged = [
      ...slabs.map((s) => ({ ...s, _kind: "slab" })),
      ...remnants.map((r) => ({ ...r, _kind: "remnant" })),
    ];
    const term = q.trim().toLowerCase();
    const base = needsPhoto
      ? merged.filter((it) => it._kind === "remnant" && !firstPhoto(it))
      : merged;
    if (!term) return base;
    return base.filter((it) =>
      [it.slab_color, it.brand, it.supplier, it.material, it.location, it.code]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(term)),
    );
  }, [slabs, remnants, q, needsPhoto]);

  const photoQueue = useMemo(() => remnants.filter((r) => !firstPhoto(r)).length, [remnants]);

  const onHand = slabStats?.in_stock ?? slabStats?.slabs ?? 0;
  const remnantCount = remStats?.total ?? remnants.length;
  const allocated = slabStats?.reserved ?? 0;
  const total = slabs.length + remnants.length;

  return (
    <div className="px-4 pt-5" data-testid="mobile-stock">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="m-title m-display text-3xl text-[#c39e6d]">Stock</h1>
          <p className="mt-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-500">
            Shop floor inventory
          </p>
        </div>
        <div className="mt-1 flex shrink-0 gap-2">
          <button
            type="button"
            data-testid="mobile-stock-add"
            onClick={() => {
              setForm(blankForm());
              setEditItem(null);
              setAdding(true);
            }}
            className="inline-flex items-center gap-1.5 rounded-xl bg-[#c39e6d] px-3 py-2.5 text-[11px] font-bold uppercase tracking-widest text-slate-950 transition-colors duration-150 hover:bg-[#c39e6d]"
          >
            <Plus size={15} weight="bold" /> Add
          </button>
          <button
            type="button"
            data-testid="mobile-stock-scan"
            onClick={() => setScanOpen(true)}
            aria-label="Scan barcode"
            className="inline-flex items-center justify-center rounded-xl border border-zinc-700 bg-[#0F172A] px-3 py-2.5 text-zinc-200 transition-colors duration-150 hover:bg-zinc-800"
          >
            <Barcode size={16} weight="bold" />
          </button>
          <button
            type="button"
            data-testid="mobile-stock-capture"
            onClick={() => fileRef.current?.click()}
            disabled={capturing}
            className="inline-flex items-center gap-2 rounded-xl border border-[#c39e6d]/50 bg-[#c39e6d]/15 px-3 py-2.5 text-[11px] font-bold uppercase tracking-widest text-[#c39e6d] transition-colors duration-150 hover:bg-[#c39e6d]/25 disabled:opacity-40"
          >
            <Camera size={15} weight="bold" /> {capturing ? "…" : "Cam"}
          </button>
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={onCapture}
          data-testid="mobile-stock-capture-input"
        />
      </div>

      <label className="mt-4 flex h-12 items-center gap-2.5 rounded-2xl border border-zinc-800 bg-[#0F172A]/70 px-3.5">
        <MagnifyingGlass size={18} weight="bold" className="shrink-0 text-zinc-500" />
        <input
          data-testid="mobile-stock-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search slabs, racks, notes…"
          className="min-w-0 flex-1 bg-transparent text-[15px] text-zinc-100 placeholder:text-zinc-600 focus:outline-none"
        />
      </label>

      <div className="mt-3 grid grid-cols-3 gap-2" data-testid="mobile-stock-stats">
        <StatCard value={onHand} label="On-hand" tone="text-[#c39e6d]" testid="mobile-stock-onhand" />
        <StatCard value={remnantCount} label="Remnants" tone="text-amber-300" testid="mobile-stock-remnants" />
        <StatCard value={allocated} label="Allocated" tone="text-emerald-300" testid="mobile-stock-allocated" />
      </div>

      <div className="mt-5 flex items-center justify-between">
        <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-zinc-500">
          Showing {items.length} of {total}
        </span>
        <button
          type="button"
          data-testid="mobile-stock-needs-photo"
          onClick={() => setNeedsPhoto((v) => !v)}
          className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-[10px] font-bold uppercase tracking-[0.16em] transition-colors duration-150 ${
            needsPhoto
              ? "border-amber-400/60 bg-amber-500/15 text-amber-200"
              : "border-zinc-800 text-zinc-500 hover:text-zinc-300"
          }`}
        >
          <Camera size={13} weight="bold" /> Needs photo{photoQueue ? ` · ${photoQueue}` : ""}
        </button>
      </div>

      {items.length === 0 ? (
        <div
          data-testid="mobile-stock-empty"
          className="mt-6 rounded-2xl border border-dashed border-zinc-800 p-8 text-center text-sm text-zinc-500"
        >
          No inventory matches your search.
        </div>
      ) : (
        <div className="mt-3 grid grid-cols-2 gap-3" data-testid="mobile-stock-grid">
          {items.map((it) => (
            <StockCard key={`${it._kind}-${it.id}`} item={it} onOpen={setDetail} />
          ))}
        </div>
      )}

      {tag && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4"
          data-testid="mobile-stock-tag"
          onClick={() => setTag(null)}
        >
          <div
            className="w-full max-w-md rounded-2xl border border-zinc-800 bg-[#0F172A] p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#c39e6d]">
              Tag this slab
            </div>
            <label className="mt-3 block text-[10px] font-bold uppercase tracking-widest text-zinc-500">
              Name
            </label>
            <input
              autoFocus
              value={tag.name}
              onChange={(e) => setTag((t) => ({ ...t, name: e.target.value }))}
              data-testid="mobile-stock-tag-name"
              placeholder="e.g. Calacatta offcut"
              className="mt-1 h-11 w-full rounded-xl border border-zinc-800 bg-[#080C14] px-3 text-[15px] text-zinc-100 placeholder:text-zinc-600 focus:border-[#c39e6d] focus:outline-none"
            />
            <label className="mt-3 block text-[10px] font-bold uppercase tracking-widest text-zinc-500">
              Material
            </label>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {["Granite", "Quartz", "Quartzite", "Marble", "Porcelain"].map((mt) => (
                <button
                  key={mt}
                  type="button"
                  data-testid={`mobile-stock-tag-material-${mt}`}
                  onClick={() => setTag((t) => ({ ...t, material: mt }))}
                  className={`rounded-lg border px-3 py-2 text-[11px] font-bold uppercase tracking-widest transition-colors duration-150 ${
                    tag.material === mt
                      ? "border-[#c39e6d] bg-[#c39e6d]/15 text-[#c39e6d]"
                      : "border-zinc-800 bg-[#080C14] text-zinc-400 hover:border-zinc-700"
                  }`}
                >
                  {mt}
                </button>
              ))}
            </div>
            <label className="mt-3 block text-[10px] font-bold uppercase tracking-widest text-zinc-500">
              Rack / location
            </label>
            <input
              value={tag.location}
              onChange={(e) => setTag((t) => ({ ...t, location: e.target.value }))}
              data-testid="mobile-stock-tag-location"
              placeholder="e.g. Rack B3"
              className="mt-1 h-11 w-full rounded-xl border border-zinc-800 bg-[#080C14] px-3 text-[15px] text-zinc-100 placeholder:text-zinc-600 focus:border-[#c39e6d] focus:outline-none"
            />
            <label className="mt-3 block text-[10px] font-bold uppercase tracking-widest text-zinc-500">
              Size (inches)
            </label>
            <div className="mt-1 grid grid-cols-2 gap-2">
              <input
                inputMode="decimal"
                value={tag.length}
                onChange={(e) => setTag((t) => ({ ...t, length: e.target.value }))}
                data-testid="mobile-stock-tag-length"
                placeholder="Length"
                className="h-11 w-full rounded-xl border border-zinc-800 bg-[#080C14] px-3 text-[15px] text-zinc-100 placeholder:text-zinc-600 focus:border-[#c39e6d] focus:outline-none"
              />
              <input
                inputMode="decimal"
                value={tag.width}
                onChange={(e) => setTag((t) => ({ ...t, width: e.target.value }))}
                data-testid="mobile-stock-tag-width"
                placeholder="Width"
                className="h-11 w-full rounded-xl border border-zinc-800 bg-[#080C14] px-3 text-[15px] text-zinc-100 placeholder:text-zinc-600 focus:border-[#c39e6d] focus:outline-none"
              />
            </div>
            {parseFloat(tag.length) > 0 && parseFloat(tag.width) > 0 && (
              <p className="mt-1.5 font-mono-data text-[11px] text-emerald-300" data-testid="mobile-stock-tag-sqft">
                {Math.round(((parseFloat(tag.length) * parseFloat(tag.width)) / 144) * 10) / 10} ft² usable
              </p>
            )}
            <label className="mt-3 block text-[10px] font-bold uppercase tracking-widest text-zinc-500">
              Thickness
            </label>
            <div className="mt-1 grid grid-cols-2 gap-2">
              {["2cm", "3cm"].map((tk) => (
                <button
                  key={tk}
                  type="button"
                  data-testid={`mobile-stock-tag-thickness-${tk}`}
                  onClick={() => setTag((t) => ({ ...t, thickness: tk }))}
                  className={`h-11 rounded-xl border text-sm font-bold uppercase tracking-widest transition-colors duration-150 ${
                    tag.thickness === tk
                      ? "border-[#c39e6d] bg-[#c39e6d]/15 text-[#c39e6d]"
                      : "border-zinc-800 bg-[#080C14] text-zinc-400 hover:border-zinc-700"
                  }`}
                >
                  {tk}
                </button>
              ))}
            </div>
            <div className="mt-4 flex gap-2">
              <button
                type="button"
                onClick={() => setTag(null)}
                data-testid="mobile-stock-tag-skip"
                className="h-11 flex-1 rounded-xl border border-zinc-700 text-[11px] font-bold uppercase tracking-widest text-zinc-300 hover:bg-zinc-800"
              >
                Skip
              </button>
              <button
                type="button"
                onClick={saveTag}
                data-testid="mobile-stock-tag-save"
                className="h-11 flex-1 rounded-xl bg-[#c39e6d] text-[11px] font-bold uppercase tracking-widest text-slate-950 hover:bg-[#c39e6d]"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {detail && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4"
          data-testid="mobile-stock-detail"
          onClick={() => setDetail(null)}
        >
          <div
            className="max-h-[88vh] w-full max-w-md overflow-y-auto rounded-3xl border border-zinc-800 bg-[#0F172A]"
            onClick={(e) => e.stopPropagation()}
          >
            {firstPhoto(detail) ? (
              <img
                src={firstPhoto(detail)}
                alt={detail.slab_color}
                className="aspect-[5/4] w-full rounded-t-3xl object-cover"
              />
            ) : (
              <button
                type="button"
                data-testid="mobile-stock-detail-snap-hero"
                onClick={() => detailFileRef.current?.click()}
                className="flex aspect-[5/4] w-full flex-col items-center justify-center gap-2 rounded-t-3xl border-b border-dashed border-amber-500/40 bg-gradient-to-br from-zinc-700/40 to-zinc-900 text-xs font-bold uppercase tracking-widest text-amber-200"
              >
                <Camera size={28} weight="bold" />
                {snapping ? "Uploading…" : "Tap to snap the stone"}
              </button>
            )}
            <input
              ref={detailFileRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              data-testid="mobile-stock-detail-photo-input"
              onChange={snapDetailPhoto}
            />
            <div className="p-4">
              <div className="flex items-center justify-between gap-2">
                <span
                  className={`rounded-md border px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${
                    detail._kind === "remnant"
                      ? "border-amber-500/40 bg-amber-500/15 text-amber-200"
                      : "border-zinc-700 bg-[#080C14]/70 text-zinc-200"
                  }`}
                >
                  {detail._kind === "remnant" ? "Remnant" : detail.status || "Slab"}
                </span>
                <button
                  type="button"
                  data-testid="mobile-stock-detail-snap"
                  disabled={snapping}
                  onClick={() => detailFileRef.current?.click()}
                  className="ml-auto rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-amber-200 hover:bg-amber-500/20 disabled:opacity-40"
                >
                  <Camera size={13} weight="bold" className="mr-1 inline" />
                  {snapping ? "…" : firstPhoto(detail) ? "Add photo" : "Snap photo"}
                </button>
                <button
                  type="button"
                  data-testid="mobile-stock-detail-edit"
                  onClick={() => {
                    setForm(fromItem(detail));
                    setEditItem(detail);
                    setAdding(true);
                    setDetail(null);
                  }}
                  className="mr-2 rounded-lg border border-[#c39e6d]/40 bg-[#c39e6d]/10 px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-[#c39e6d] hover:bg-[#c39e6d]/20"
                >
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => setDetail(null)}
                  data-testid="mobile-stock-detail-close"
                  className="flex h-8 w-8 items-center justify-center rounded-full border border-zinc-700 text-zinc-400 hover:text-zinc-200"
                >
                  <X size={15} weight="bold" />
                </button>
              </div>
              {(detail.brand || detail.supplier) && (
                <div className="mt-3 text-[11px] font-bold uppercase tracking-widest text-[#c39e6d]">
                  {detail.brand || detail.supplier}
                </div>
              )}
              <h2 className="mt-0.5 text-xl font-bold text-zinc-50">
                {detail.slab_color || "Unnamed"}
              </h2>
              {detail.code && (
                <div className="mt-0.5 font-mono-data text-[11px] text-zinc-500">{detail.code}</div>
              )}
              <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3">
                {[
                  ["Material", detail.material],
                  ["Thickness", detail.thickness],
                  ["Finish", detail.finish],
                  ["Dimensions", dims(detail) || "—"],
                  detail._kind === "remnant"
                    ? ["Usable", detail.usable_sqft ? `${detail.usable_sqft} ft²` : "—"]
                    : ["Quantity", String(detail.quantity ?? "—")],
                  ["Location", detail.location || "—"],
                  ["Supplier", detail.supplier || "—"],
                  ["Status", detail.status || (detail._kind === "remnant" ? "Available" : "—")],
                ].map(([k, v]) => (
                  <div key={k}>
                    <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-600">
                      {k}
                    </div>
                    <div className="mt-0.5 truncate text-sm text-zinc-200">{v || "—"}</div>
                  </div>
                ))}
              </div>
              {detail.notes && (
                <div className="mt-4">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-600">
                    Notes
                  </div>
                  <p className="mt-1 whitespace-pre-line text-sm text-zinc-300">{detail.notes}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {adding && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4"
          data-testid="mobile-stock-add-sheet"
          onClick={() => !busy && setAdding(false)}
        >
          <div
            className="max-h-[92vh] w-full max-w-md overflow-y-auto rounded-3xl border border-zinc-800 bg-[#0F172A] p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#c39e6d]">
              {editItem ? "Edit item" : "Add to inventory"}
            </div>
            {!editItem && (
              <div className="mt-3 grid grid-cols-2 gap-2">
                {[
                  ["slab", "Slab"],
                  ["remnant", "Remnant"],
                ].map(([k, l]) => (
                  <button
                    key={k}
                    type="button"
                    data-testid={`mobile-stock-add-kind-${k}`}
                    onClick={() => setForm((f) => ({ ...f, kind: k }))}
                    className={`h-11 rounded-xl border text-sm font-bold uppercase tracking-widest ${
                      form.kind === k
                        ? "border-[#c39e6d] bg-[#c39e6d]/15 text-[#c39e6d]"
                        : "border-zinc-800 bg-[#080C14] text-zinc-400"
                    }`}
                  >
                    {l}
                  </button>
                ))}
              </div>
            )}
            <Lbl>Material</Lbl>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {["Granite", "Quartz", "Quartzite", "Marble", "Porcelain"].map((mt) => (
                <button
                  key={mt}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, material: mt }))}
                  className={chip(form.material === mt)}
                >
                  {mt}
                </button>
              ))}
            </div>
            <Lbl>Name / color *</Lbl>
            <input
              autoFocus
              value={form.slab_color}
              onChange={(e) => setForm((f) => ({ ...f, slab_color: e.target.value }))}
              data-testid="mobile-stock-add-name"
              placeholder="e.g. Calacatta Gold"
              className={inp}
            />
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Lbl>Brand</Lbl>
                <input
                  value={form.brand}
                  onChange={(e) => setForm((f) => ({ ...f, brand: e.target.value }))}
                  className={inp}
                />
              </div>
              <div>
                <Lbl>Supplier</Lbl>
                <input
                  value={form.supplier}
                  onChange={(e) => setForm((f) => ({ ...f, supplier: e.target.value }))}
                  className={inp}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Lbl>Thickness</Lbl>
                <div className="mt-1 grid grid-cols-2 gap-1.5">
                  {["2cm", "3cm"].map((tk) => (
                    <button
                      key={tk}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, thickness: tk }))}
                      className={`h-11 rounded-xl border text-xs font-bold ${
                        form.thickness === tk
                          ? "border-[#c39e6d] bg-[#c39e6d]/15 text-[#c39e6d]"
                          : "border-zinc-800 bg-[#080C14] text-zinc-400"
                      }`}
                    >
                      {tk}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Lbl>Finish</Lbl>
                <select
                  value={form.finish}
                  onChange={(e) => setForm((f) => ({ ...f, finish: e.target.value }))}
                  className={inp}
                >
                  {["Polished", "Honed", "Leathered", "Brushed"].map((x) => (
                    <option key={x} value={x}>
                      {x}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Lbl>{form.kind === "remnant" ? "Length (in) *" : "Length (in)"}</Lbl>
                <input
                  inputMode="decimal"
                  value={form.length}
                  onChange={(e) => setForm((f) => ({ ...f, length: e.target.value }))}
                  data-testid="mobile-stock-add-length"
                  className={inp}
                />
              </div>
              <div>
                <Lbl>{form.kind === "remnant" ? "Width (in) *" : "Width (in)"}</Lbl>
                <input
                  inputMode="decimal"
                  value={form.width}
                  onChange={(e) => setForm((f) => ({ ...f, width: e.target.value }))}
                  data-testid="mobile-stock-add-width"
                  className={inp}
                />
              </div>
            </div>
            {form.kind === "remnant" &&
              parseFloat(form.length) > 0 &&
              parseFloat(form.width) > 0 && (
                <p className="mt-1.5 font-mono-data text-[11px] text-emerald-300">
                  {Math.round(((parseFloat(form.length) * parseFloat(form.width)) / 144) * 10) / 10}{" "}
                  ft² usable
                </p>
              )}
            <Lbl>Rack / location</Lbl>
            <input
              value={form.location}
              onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
              data-testid="mobile-stock-add-location"
              placeholder="e.g. Rack B3"
              className={inp}
            />
            {form.kind === "slab" && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Lbl>Status</Lbl>
                  <select
                    value={form.status}
                    onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                    className={inp}
                  >
                    {["In Stock", "Out of Stock", "Reserved", "Consumed", "Special Order"].map(
                      (x) => (
                        <option key={x} value={x}>
                          {x}
                        </option>
                      ),
                    )}
                  </select>
                </div>
                <div>
                  <Lbl>Quantity</Lbl>
                  <input
                    inputMode="numeric"
                    value={form.quantity}
                    onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))}
                    className={inp}
                  />
                </div>
              </div>
            )}
            <Lbl>Notes</Lbl>
            <textarea
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              rows={2}
              className={`${inp} h-auto py-2`}
            />
            <input
              ref={addFileRef}
              type="file"
              accept="image/*"
              className="hidden"
              data-testid="mobile-stock-add-photo-input"
              onChange={(e) => setForm((f) => ({ ...f, file: e.target.files?.[0] || null }))}
            />
            <button
              type="button"
              onClick={() => addFileRef.current?.click()}
              className="mt-3 flex h-11 w-full items-center justify-center gap-2 rounded-xl border border-zinc-700 text-[11px] font-bold uppercase tracking-widest text-zinc-300 hover:bg-zinc-800"
            >
              <Camera size={14} weight="bold" />{" "}
              {form.file ? form.file.name : "Add photo (optional)"}
            </button>
            <div className="sticky bottom-0 -mx-4 -mb-4 mt-4 flex gap-2 border-t border-zinc-800 bg-[#0F172A] px-4 py-3">
              <button
                type="button"
                onClick={() => {
                  setAdding(false);
                  setEditItem(null);
                }}
                disabled={busy}
                data-testid="mobile-stock-add-cancel"
                className="h-11 flex-1 rounded-xl border border-zinc-700 text-[11px] font-bold uppercase tracking-widest text-zinc-300 hover:bg-zinc-800 disabled:opacity-40"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveAdd}
                disabled={busy}
                data-testid="mobile-stock-add-save"
                className="h-11 flex-1 rounded-xl bg-[#c39e6d] text-[11px] font-bold uppercase tracking-widest text-slate-950 hover:bg-[#c39e6d] disabled:opacity-40"
              >
                {busy ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      <BarcodeScanDialog
        open={scanOpen}
        onOpenChange={setScanOpen}
        title="Scan a slab"
        placeholder="Barcode / serial #"
        testPrefix="mobile-stock-scan"
        onDetected={(code) => {
          if (!code) return;
          const c = code.toLowerCase();
          const hit = [
            ...slabs.map((s) => ({ ...s, _kind: "slab" })),
            ...remnants.map((r) => ({ ...r, _kind: "remnant" })),
          ].find((it) =>
            [it.barcode, it.serial_number, it.code, it.slab_color]
              .filter(Boolean)
              .some((v) => String(v).toLowerCase() === c),
          );
          setScanOpen(false);
          if (hit) {
            setDetail(hit);
          } else {
            setQ(code);
            toast.message(`No exact match — searching "${code}"`);
          }
        }}
      />
    </div>
  );
}
